import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { cn } from "../../../lib/utils";

interface PaymentSectionProps {
  t: any;
  lang: string;
  isInsurance: boolean;
  isRecurring: boolean;
  priceText: string;
  setPriceText: (text: string) => void;
  paymentStatus: "pending" | "paid";
  setPaymentStatus: (status: "pending" | "paid") => void;
  billingDay: number;
  setBillingDay: (day: number) => void;
  isProportional: boolean;
  setIsProportional: (is: boolean) => void;
  proportionalType: "amount" | "percentage";
  setProportionalType: (type: "amount" | "percentage") => void;
  firstPaymentDate: Date;
  setFirstPaymentDate: (date: Date) => void;
  proportionalPercentage: string;
  setProportionalPercentage: (perc: string) => void;
  firstPaymentText: string;
  setFirstPaymentText: (text: string) => void;
}

export function PaymentSection({
  t,
  lang,
  isInsurance,
  isRecurring,
  priceText,
  setPriceText,
  paymentStatus,
  setPaymentStatus,
  billingDay,
  setBillingDay,
  isProportional,
  setIsProportional,
  proportionalType,
  setProportionalType,
  firstPaymentDate,
  setFirstPaymentDate,
  proportionalPercentage,
  setProportionalPercentage,
  firstPaymentText,
  setFirstPaymentText,
}: PaymentSectionProps) {
  return (
    <section className="pt-8 border-t pb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>{t.createDrawer.payment.amount}</Label>
          <Input
            placeholder={isInsurance ? (lang === "pt" ? "Pago pelo convênio" : "Paid by insurance") : "0,00"}
            value={priceText}
            onChange={(e) => setPriceText(e.target.value)}
            disabled={isInsurance}
            className={cn("h-10 font-medium", isInsurance && "bg-neutral-100")}
          />
        </div>

        {!isRecurring ? (
          <div className="space-y-2">
            <Label>{t.createDrawer.payment.status}</Label>
            <Select
              value={isInsurance ? "pending" : paymentStatus}
              onValueChange={(v) => setPaymentStatus(v as "pending" | "paid")}
              disabled={isInsurance}
            >
              <SelectTrigger className={cn("h-10", isInsurance && "bg-neutral-100")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">{t.createDrawer.payment.statusOptions.pending}</SelectItem>
                <SelectItem value="paid">{t.createDrawer.payment.statusOptions.paid}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>{t.createDrawer.payment.billingDay}</Label>
            <Input
              type="number"
              min="1"
              max="28"
              value={billingDay}
              onChange={(e) => setBillingDay(Number(e.target.value))}
              className="h-10"
            />
          </div>
        )}
      </div>

      {isRecurring && (
        <div className="mt-6 p-5 rounded-lg border border-neutral-200 bg-neutral-50/50 space-y-5">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">{t.createDrawer.payment.isProportional}</Label>
            <input
              type="checkbox"
              checked={isProportional}
              onChange={(e) => setIsProportional(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 accent-neutral-900"
            />
          </div>
          {isProportional && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-neutral-500 font-medium uppercase tracking-wider">
                    {t.createDrawer.payment.proportionalType}
                  </Label>
                  <Select
                    value={proportionalType}
                    onValueChange={(v) => setProportionalType(v as "amount" | "percentage")}
                  >
                    <SelectTrigger className="h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="amount">{t.createDrawer.payment.typeAmount}</SelectItem>
                      <SelectItem value="percentage">{t.createDrawer.payment.typePercentage}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-neutral-500 font-medium uppercase tracking-wider">
                    {t.createDrawer.payment.firstPaymentDate}
                  </Label>
                  <Input
                    type="date"
                    className="h-10 bg-white"
                    value={firstPaymentDate.toISOString().split("T")[0]}
                    onChange={(e) => setFirstPaymentDate(new Date(e.target.value + "T00:00:00"))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 font-medium uppercase tracking-wider">
                  {proportionalType === "percentage"
                    ? t.createDrawer.payment.firstPaymentPercentage
                    : t.createDrawer.payment.firstPaymentAmount}
                </Label>
                {proportionalType === "percentage" ? (
                  <Input
                    value={proportionalPercentage}
                    onChange={(e) => setProportionalPercentage(e.target.value)}
                    placeholder="100"
                    className="h-10 bg-white"
                  />
                ) : (
                  <Input
                    value={firstPaymentText}
                    onChange={(e) => setFirstPaymentText(e.target.value)}
                    className="h-10 bg-white"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {isRecurring && priceText && (
        <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-100 flex gap-3">
          <div className="h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-[10px] text-white font-bold">i</span>
          </div>
          <p className="text-sm text-emerald-800 leading-relaxed">
            {t.createDrawer.payment.billingSummary(
              isProportional
                ? firstPaymentDate.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US")
                : `${billingDay}/${(new Date().getMonth() + (new Date().getDate() > billingDay ? 2 : 1))
                    .toString()
                    .padStart(2, "0")}`,
              isProportional ? firstPaymentText : priceText,
              priceText,
              billingDay
            )}
          </p>
        </div>
      )}
    </section>
  );
}
